import type { DataGridProProps, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Button } from '@tempo-io/sui-button';
import { Dependency, DoNotDisturbOn } from '@tempo-io/sui-icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@tempo-io/sui-tooltip';
import type { TFunction } from 'i18next';
import { type ItemHealth, ItemHealthLevel } from '@/common/api/types/dashboard/health';
import type { ItemStatus } from '@/common/api/types/itemStatus';
import type { ItemType } from '@/common/api/types/itemType';
import { formatLocalDate } from '@/common/utils/dateFormats';
import { DependencyNameCell } from '@/features/item-panel/components/tabs/dependencies/DependencyNameCell';
import { DependencyPredecessorCell } from '@/features/item-panel/components/tabs/dependencies/DependencyPredecessorCell';
import { DependencySuccessorCell } from '@/features/item-panel/components/tabs/dependencies/DependencySuccessorCell';
import { HealthCell } from '@/features/item-panel/components/tabs/dependencies/HealthCell';
import {
  HEALTH_LEVEL_SORT_RANK,
  MIN_NAME_COLUMN_WIDTH,
} from '@/features/item-panel/components/tabs/dependencies/lib/constants';
import type {
  DependencyRow,
  DependencySectionValue,
} from '@/features/item-panel/components/tabs/dependencies/lib/types';
import { StatusCell } from '@/features/item-panel/components/tabs/dependencies/StatusCell';
import type { ItemsTableRow } from '@/features/items/components/table/types';

export const buildGroupingColDef = (
  nameHeader: string,
  itemTypes: ItemType[],
): DataGridProProps<ItemsTableRow>['groupingColDef'] => ({
  headerName: nameHeader,
  valueGetter: (_value, row: ItemsTableRow) => row.name,
  renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
    <DependencyNameCell row={params.row} itemTypes={itemTypes} />
  ),
  minWidth: MIN_NAME_COLUMN_WIDTH,
  flex: 1,
  hideDescendantCount: true,
  hideable: false,
  sortable: true,
  disableColumnMenu: false,
  pinnable: false,
});

interface BuildColumnsOptions {
  section: DependencySectionValue;
  currentItemId: number;
  currentItemHealth: ItemHealth | undefined;
  healthByItemId: Map<number, ItemHealth | undefined>;
  itemStatuses: ItemStatus[];
  canRemove: boolean;
  isRemoveInFlight: boolean;
  inheritedItemIds: ReadonlySet<number>;
  onRemove: (section: DependencySectionValue, rowItemId: number) => void;
  t: TFunction;
}

const buildDependencyStatusColumn = ({
  section,
  currentItemId,
  currentItemHealth,
  healthByItemId,
  t,
}: BuildColumnsOptions): GridColDef<ItemsTableRow> => ({
  field: 'dependencyStatus',
  headerName: '',
  width: 56,
  sortable: false,
  disableColumnMenu: true,
  renderHeader: () => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={t(($) => $.adaptivePlanner.itemPanel.dependencies)}
            className="flex w-full cursor-default items-center justify-center text-text-brand-secondary"
          >
            <Dependency />
          </button>
        </TooltipTrigger>
        <TooltipContent>{t(($) => $.adaptivePlanner.itemPanel.dependencies)}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ),
  renderCell: (params: GridRenderCellParams<ItemsTableRow>) =>
    section === 'predecessors' ? (
      <DependencyPredecessorCell row={params.row} currentItemHealth={currentItemHealth} />
    ) : (
      <DependencySuccessorCell
        row={params.row}
        currentItemId={currentItemId}
        healthByItemId={healthByItemId}
      />
    ),
});

const buildStatusColumn = ({
  itemStatuses,
  t,
}: BuildColumnsOptions): GridColDef<ItemsTableRow> => ({
  field: 'status',
  headerName: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.status),
  width: 140,
  sortable: true,
  valueGetter: (_value, row: ItemsTableRow) =>
    itemStatuses.find((itemStatus) => itemStatus.id === row.itemStatusId)?.priority ?? '',
  renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
    <StatusCell row={params.row} itemStatuses={itemStatuses} />
  ),
});

const buildHealthColumn = ({
  healthByItemId,
  t,
}: BuildColumnsOptions): GridColDef<ItemsTableRow> => ({
  field: 'health',
  headerName: t(($) => $.views.table.columns.systemFields.health),
  width: 140,
  sortable: true,
  valueGetter: (_value, row: ItemsTableRow) =>
    HEALTH_LEVEL_SORT_RANK[healthByItemId.get(row.id)?.healthLevel ?? ItemHealthLevel.DONE],
  renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
    <HealthCell health={healthByItemId.get(params.row.id)} />
  ),
});

const buildPlannedDateColumn = ({ section, t }: BuildColumnsOptions): GridColDef<ItemsTableRow> =>
  section === 'successors'
    ? {
        field: 'plannedStart',
        headerName: t(($) => $.views.table.columns.systemFields.plannedStartDate),
        width: 140,
        sortable: true,
        valueGetter: (_value, row: ItemsTableRow) => row.plannedStartDateTime ?? '',
        renderCell: (params: GridRenderCellParams<ItemsTableRow>) =>
          params.row.plannedStartDateTime ? formatLocalDate(params.row.plannedStartDateTime) : '-',
      }
    : {
        field: 'plannedFinish',
        headerName: t(($) => $.views.table.columns.systemFields.plannedFinishDate),
        width: 140,
        sortable: true,
        valueGetter: (_value, row: ItemsTableRow) => row.plannedFinishDateTime ?? '',
        renderCell: (params: GridRenderCellParams<ItemsTableRow>) =>
          params.row.plannedFinishDateTime
            ? formatLocalDate(params.row.plannedFinishDateTime)
            : '-',
      };

const buildForecastedDateColumn = ({
  section,
  t,
}: BuildColumnsOptions): GridColDef<DependencyRow> =>
  section === 'successors'
    ? {
        field: 'forecastedStart',
        headerName: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.forecastedStart),
        width: 170,
        sortable: true,
        valueGetter: (_value, row: DependencyRow) => row.forecastedStartDateTime ?? '',
        renderCell: (params: GridRenderCellParams<DependencyRow>) =>
          params.row.forecastedStartDateTime
            ? formatLocalDate(params.row.forecastedStartDateTime)
            : '-',
      }
    : {
        field: 'forecastedFinish',
        headerName: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.forecastedFinish),
        width: 170,
        sortable: true,
        valueGetter: (_value, row: DependencyRow) => row.forecastedFinishDateTime ?? '',
        renderCell: (params: GridRenderCellParams<DependencyRow>) =>
          params.row.forecastedFinishDateTime
            ? formatLocalDate(params.row.forecastedFinishDateTime)
            : '-',
      };

const buildRemoveColumn = ({
  section,
  isRemoveInFlight,
  inheritedItemIds,
  onRemove,
  t,
}: BuildColumnsOptions): GridColDef<ItemsTableRow> => ({
  field: 'actions',
  headerName: '',
  width: 48,
  sortable: false,
  disableColumnMenu: true,
  renderCell: (params: GridRenderCellParams<ItemsTableRow>) => {
    const isInherited = inheritedItemIds.has(params.row.id);
    const isDisabled = isInherited || isRemoveInFlight;
    return (
      <div className="flex justify-end pr-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (!isInherited) {
                      onRemove(section, params.row.id);
                    }
                  }}
                  disabled={isDisabled}
                  aria-label={t(
                    ($) =>
                      $.adaptivePlanner.itemPanel.dependenciesSection.removeDependencyAriaLabel,
                    { itemName: params.row.name ?? '' },
                  )}
                  className="shrink-0 text-text-neutral-secondary"
                >
                  <DoNotDisturbOn />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {isInherited
                ? t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.cannotRemoveInherited)
                : t(($) => $.common.remove)}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  },
});

export const buildColumns = (options: BuildColumnsOptions): GridColDef<DependencyRow>[] => {
  const columns: GridColDef<DependencyRow>[] = [
    buildDependencyStatusColumn(options),
    buildStatusColumn(options),
    buildHealthColumn(options),
    buildPlannedDateColumn(options),
    buildForecastedDateColumn(options),
  ];
  if (options.canRemove) {
    columns.push(buildRemoveColumn(options));
  }
  return columns;
};
